/**
 * Currency utility functions for normalizing and formatting currencies
 */

/**
 * Maps non-standard currency codes to ISO 4217 codes
 */
const CURRENCY_MAP: { [key: string]: string } = {
  'TL': 'TRY',  // Turkish Lira
  'USD': 'USD', // US Dollar
  'EUR': 'EUR', // Euro
  'GBP': 'GBP', // British Pound
  'CHF': 'CHF', // Swiss Franc
  'JPY': 'JPY', // Japanese Yen
  'CAD': 'CAD', // Canadian Dollar
  'AUD': 'AUD', // Australian Dollar
};

/**
 * Normalizes a currency code to ISO 4217 standard
 * @param currency - The currency code to normalize
 * @returns The normalized currency code
 */
export const normalizeCurrency = (currency: string): string => {
  if (!currency) return 'USD';
  return CURRENCY_MAP[currency.toUpperCase()] || currency.toUpperCase();
};

/**
 * Formats a currency amount using Intl.NumberFormat
 * @param amount - The amount to format
 * @param currency - The currency code
 * @param options - Additional formatting options
 * @returns The formatted currency string
 */
export const formatCurrency = (
  amount: number, 
  currency: string = 'USD',
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    locale?: string;
  } = {}
): string => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = 'en-US'
  } = options;

  const normalizedCurrency = normalizeCurrency(currency);
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(amount || 0);
  } catch (error) {
    // Fallback to simple formatting if currency is still invalid
    console.warn(`Currency formatting failed for ${normalizedCurrency}:`, error);
    return `${normalizedCurrency} ${(amount || 0).toLocaleString()}`;
  }
};

/**
 * Gets the currency symbol for a given currency code
 * @param currency - The currency code
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const normalizedCurrency = normalizeCurrency(currency);
  
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    // Extract symbol from formatted string
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart?.value || normalizedCurrency;
  } catch (error) {
    // Fallback symbols
    const fallbackSymbols: { [key: string]: string } = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CHF': 'CHF',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
    };
    
    return fallbackSymbols[normalizedCurrency] || normalizedCurrency;
  }
};
